// Widget_UpcomingCompliance — task 5.3 of the dashboard-redesign spec
// (Requirement 9). A read-only list of compliance items expiring in the
// next 90 days (or already expired), each with a severity badge.
//
// This widget is READ-ONLY. It:
//   1. consumes `useUpcomingCompliance(scope, 90)` (task 2.5) which
//      returns `WidgetResult<UpcomingComplianceRow[]>` — rows arrive
//      already sorted ascending by `daysUntilExpiry` with severity
//      derived per Req 9.2;
//   2. renders the four lifecycle states (loading, empty, ready, error)
//      via `<WidgetFrame>` so the layout doesn't reflow (Req 3.5);
//   3. deep-links per row to `/dashboard/compliance/register?id={id}` and
//      exposes a "View all N items →" link when the underlying list
//      exceeds `limit` (default 5); and
//   4. is wrapped in `<ErrorBoundary>` so a render-time exception cannot
//      blank the surrounding dashboard (Req 3.6, Property P4).
//
// Severity badge tokens are taken verbatim from
// `.kiro/specs/dashboard-redesign/requirements.md` §9.2 and match the
// design-mockup.svg row visuals:
//   - EXPIRED (days < 0)              → bg `#fecaca` / text `#991b1b`
//   - URGENT  (0 ≤ days ≤ 30)         → bg `#fee2e2` / text `#991b1b`
//   - SOON    (30 < days ≤ 90)        → bg `#fef3c7` / text `#92400e`
//
// The empty state carries the exact copy from Req 9.3
// ("No compliance items expiring in the next 90 days.") plus an "Add
// compliance items" CTA deep-linking to the register.

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetError } from "../WidgetError";
import { WidgetSkeleton } from "../WidgetSkeleton";
import { useUpcomingCompliance } from "../hooks/useUpcomingCompliance";
import type {
  BuildingScope,
  UpcomingComplianceRow,
  WidgetResult,
} from "../types";

/** Widget props — every dashboard widget takes exactly `scope`. */
export interface WidgetUpcomingComplianceProps {
  scope: BuildingScope;
  /** Maximum number of rows to render before overflow. Defaults to 5. */
  limit?: number;
}

/** Default number of rows shown before the "View all" overflow link. */
const DEFAULT_LIMIT = 5;

/** Days lookahead passed to the hook — mirrors Req 9.1 (next 90 days). */
const DAYS_WINDOW = 90;

/** Card title (Req 9 header copy — matches design-mockup.svg). */
const WIDGET_TITLE = "Upcoming Compliance";

/** Empty-state copy (Req 9.3 — exact wording). */
const EMPTY_COPY = "No compliance items expiring in the next 90 days.";

/** Empty-state CTA copy (Req 9.3). */
const EMPTY_CTA = "Add compliance items";

/** Route the widget deep-links into (matches App.tsx `/dashboard/compliance/register`). */
const REGISTER_ROUTE = "/dashboard/compliance/register";

/**
 * Colour tokens for each severity badge. Matched to Req 9.2 and design
 * language semantic pill styling.
 */
const BADGE_TOKENS: Readonly<
  Record<
    UpcomingComplianceRow["severity"],
    { label: string; bg: string; fg: string }
  >
> = {
  expired: { label: "EXPIRED", bg: "#fecaca", fg: "#991b1b" },
  urgent: { label: "URGENT", bg: "#fee2e2", fg: "#991b1b" },
  soon: { label: "SOON", bg: "#fef3c7", fg: "#92400e" },
};

/**
 * Stable three-letter month abbreviations. Hard-coded rather than derived
 * from `Intl.DateTimeFormat` so the visual is deterministic across Node
 * versions (Node 20 returns "Sept" for September in the en-GB locale,
 * whereas the design mockup — and this widget — commit to "Sep").
 */
const MONTHS_ABBR: readonly string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format a compliance row's `nextDue` ISO string as a short display date
 * such as "04 Sep 2026". Falls back to the raw string if parsing fails.
 */
function formatDueDate(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const d = new Date(parsed);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = MONTHS_ABBR[d.getUTCMonth()] ?? "";
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Format the days marker suffix.
 *
 *   days === 0  → "(today)"
 *   days > 0    → "({days}d)"       e.g. "(11d)"
 *   days < 0    → "({days}d)"       e.g. "(-9d)"
 *
 * The design-mockup uses `(±d)` with a signed integer inside parentheses.
 */
function formatDaysMarker(days: number): string {
  if (days === 0) return "(today)";
  return `(${days}d)`;
}

/**
 * Build the per-row deep-link href. Task 5.3 specifies
 * `/dashboard/compliance/register?id=<complianceId>`.
 */
function buildRowHref(complianceId: string): string {
  return `${REGISTER_ROUTE}?id=${encodeURIComponent(complianceId)}`;
}

/**
 * Runtime narrowing accessor for optional building-name context. The
 * canonical shared `ComplianceRegister` model doesn't (yet) carry a
 * `buildingName` field — the endpoint may extend the payload — so this
 * reads it defensively without widening the shared type.
 *
 * Falls back to `responsibleParty`, then to `"—"` so a row always renders
 * a value in the building column instead of an empty gap.
 */
function readBuildingLabel(item: UpcomingComplianceRow["item"]): string {
  if (!item || typeof item !== "object") return "—";
  const record = item as unknown as Record<string, unknown>;
  const buildingName = record.buildingName;
  if (typeof buildingName === "string" && buildingName.length > 0) {
    return buildingName;
  }
  const responsibleParty = record.responsibleParty;
  if (typeof responsibleParty === "string" && responsibleParty.length > 0) {
    return responsibleParty;
  }
  return "—";
}

/** Narrow, presentation-only fields extracted from the widened item shape. */
interface DisplayFields {
  id: string;
  name: string;
  buildingLabel: string;
  nextDue: string;
}

function readDisplayFields(item: UpcomingComplianceRow["item"]): DisplayFields {
  const record = (item as unknown as Record<string, unknown>) ?? {};
  const id = typeof record.id === "string" ? record.id : "";
  const name = typeof record.name === "string" ? record.name : "";
  const nextDue = typeof record.nextDue === "string" ? record.nextDue : "";
  return {
    id,
    name,
    buildingLabel: readBuildingLabel(item),
    nextDue,
  };
}

/**
 * Ready-state body: the header, the row list, and (optionally) the "View
 * all N items →" overflow link.
 */
function ReadyContent({
  rows,
  limit,
}: {
  rows: UpcomingComplianceRow[];
  limit: number;
}): JSX.Element {
  const visibleRows = rows.slice(0, limit);
  const hasOverflow = rows.length > limit;

  return (
    <div
      data-testid="widget-upcoming-compliance-list"
      className="flex flex-col divide-y"
      style={{ borderColor: "#f3f4f6" }}
    >
      {visibleRows.map((row) => {
        const fields = readDisplayFields(row.item);
        const badge = BADGE_TOKENS[row.severity];
        const dateText = fields.nextDue ? formatDueDate(fields.nextDue) : "—";
        const daysText = formatDaysMarker(row.daysUntilExpiry);
        const isExpired = row.severity === "expired";
        return (
          <Link
            key={fields.id || `${fields.name}-${fields.nextDue}`}
            to={buildRowHref(fields.id)}
            data-testid="widget-upcoming-compliance-row"
            data-severity={row.severity}
            data-compliance-id={fields.id}
            className="flex items-center justify-between gap-3 py-2 px-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
          >
            <div className="min-w-0 flex-1">
              <div
                data-testid="widget-upcoming-compliance-name"
                className="text-sm font-semibold truncate"
                style={{ color: "#111827" }}
              >
                {fields.name || "—"}
              </div>
              <div
                data-testid="widget-upcoming-compliance-building"
                className="text-xs truncate"
                style={{ color: "#6b7280" }}
              >
                {fields.buildingLabel}
              </div>
            </div>
            <div
              data-testid="widget-upcoming-compliance-expires"
              className="text-xs whitespace-nowrap"
              style={{ color: isExpired ? "#dc2626" : "#374151" }}
            >
              {dateText} {daysText}
            </div>
            <span
              data-testid="widget-upcoming-compliance-badge"
              data-severity={row.severity}
              className="inline-flex items-center justify-center text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5"
              style={{ backgroundColor: badge.bg, color: badge.fg }}
            >
              {badge.label}
            </span>
          </Link>
        );
      })}
      {hasOverflow && (
        <Link
          to={REGISTER_ROUTE}
          data-testid="widget-upcoming-compliance-view-all"
          className="text-xs px-3 py-2 hover:underline"
          style={{ color: "#2563eb" }}
        >
          View all {rows.length} items →
        </Link>
      )}
    </div>
  );
}

/** Header row: title + the widget-level deep-link to the register. */
function WidgetHeader(): JSX.Element {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <div>
        <h2 className="text-sm font-semibold" style={{ color: "#111827" }}>
          {WIDGET_TITLE}
        </h2>
        <p className="text-xs" style={{ color: "#6b7280" }}>
          Certificates expiring in the next {DAYS_WINDOW} days
        </p>
      </div>
      <Link
        to={REGISTER_ROUTE}
        data-testid="widget-upcoming-compliance-header-link"
        className="text-xs hover:underline"
        style={{ color: "#2563eb" }}
      >
        See all →
      </Link>
    </div>
  );
}

/**
 * The empty-state body carries the exact Req 9.3 copy plus a CTA link
 * that navigates to the compliance register (where a treasurer can add
 * new items). This is rendered instead of the default `<WidgetFrame>`
 * empty state because Req 9.3 requires a specific string and CTA rather
 * than the generic "No data yet".
 */
function EmptyBody(): JSX.Element {
  return (
    <div
      role="status"
      data-testid="widget-upcoming-compliance-empty"
      className="flex flex-col items-center justify-center gap-2 py-6 text-center"
    >
      <p className="text-sm" style={{ color: "#6b7280" }}>
        {EMPTY_COPY}
      </p>
      <Link
        to={REGISTER_ROUTE}
        data-testid="widget-upcoming-compliance-empty-cta"
        className="text-xs font-medium hover:underline"
        style={{ color: "#2563eb" }}
      >
        {EMPTY_CTA} →
      </Link>
    </div>
  );
}

/**
 * Render exactly one of the four lifecycle branches. We deliberately
 * inline the state switch (rather than wrapping the whole card in
 * `<WidgetFrame>`) so the header + card chrome stay stable across states
 * and the empty state can carry its Req 9.3-mandated CTA — the generic
 * `<WidgetFrame>` empty state doesn't compose a link.
 */
function WidgetBody({
  result,
  limit,
}: {
  result: WidgetResult<UpcomingComplianceRow[]>;
  limit: number;
}): JSX.Element {
  if (result.status === "loading") {
    return <WidgetSkeleton height={160} />;
  }
  if (result.status === "empty") {
    return <EmptyBody />;
  }
  if (result.status === "error") {
    return (
      <WidgetError
        errorId={result.errorId}
        name={WIDGET_TITLE}
        onRetry={result.retry}
      />
    );
  }
  // status === "ready"
  return <ReadyContent rows={result.data} limit={limit} />;
}

/**
 * Inner component — subscribes to the hook and renders the card body.
 * Kept as a separate function so the `<ErrorBoundary>` in the outer
 * `Widget_UpcomingCompliance` sits above it in the tree and can catch a
 * render-time exception thrown by the hook itself.
 */
function WidgetUpcomingComplianceInner({
  scope,
  limit,
}: {
  scope: BuildingScope;
  limit: number;
}): JSX.Element {
  const result = useUpcomingCompliance(scope, DAYS_WINDOW);
  return (
    <div
      data-testid="widget-upcoming-compliance"
      className="bg-white rounded-xl border p-4 h-full flex flex-col"
      style={{ borderColor: "#e5e7eb" }}
    >
      <WidgetHeader />
      <div className="flex-1 min-h-0">
        <WidgetBody result={result} limit={limit} />
      </div>
    </div>
  );
}

/**
 * Widget_UpcomingCompliance — a list widget surfacing compliance items
 * expiring in the next 90 days (or already expired), with severity
 * badges per Req 9.2.
 */
export function Widget_UpcomingCompliance({
  scope,
  limit = DEFAULT_LIMIT,
}: WidgetUpcomingComplianceProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <div
          data-testid="widget-upcoming-compliance"
          className="bg-white rounded-xl border p-4"
          style={{ borderColor: "#e5e7eb" }}
        >
          <WidgetHeader />
          <WidgetError
            name={WIDGET_TITLE}
            errorId={`render-${err.name || "Error"}`}
          />
        </div>
      )}
    >
      <WidgetUpcomingComplianceInner scope={scope} limit={limit} />
    </ErrorBoundary>
  );
}

export default Widget_UpcomingCompliance;
